'use client';

/**
 * Persistent storage utilities for calculator state
 */

// Storage keys for calculator state
const STORAGE_KEYS = {
  CALCULATOR_INPUTS: 'accessnow-roi-calculator-inputs',
  // Deprecated results keys - kept for cleanup of existing user data
  CALCULATOR_RESULTS_V1: 'accessnow-roi-calculator-results-v1',
  CALCULATOR_RESULTS_OLD: 'accessnow-roi-calculator-results',
  CALCULATOR_WIZARD_STEP: 'accessnow-roi-calculator-wizard-step',
  LAST_CALCULATOR_TAB: 'accessnow-roi-last-calculator-tab',
  CUSTOM_PRICING_PREFIX: 'accessnow-calculator-custom-pricing',
} as const;

// All supported currencies for clearing custom pricing keys
const SUPPORTED_CURRENCIES = ['NZD', 'AUD', 'USD', 'GBP', 'EUR', 'ZAR', 'CAD'] as const;

/**
 * Clears all calculator-related localStorage keys.
 * Used to reset calculator state on login.
 * 
 * This function clears:
 * - Calculator inputs
 * - Calculator results (deprecated, but cleared for cleanup)
 * - Wizard step
 * - Last calculator tab preference
 * - Custom pricing settings for all currencies
 * 
 * Note: This does NOT affect share links, which use encrypted URL parameters
 * and explicitly disable localStorage via the `disableLocalStorage` option.
 */
export function clearCalculatorState(): void {
  if (typeof window === 'undefined') {
    return; // Skip during SSR
  }
  
  try {
    // Clear main calculator state keys
    localStorage.removeItem(STORAGE_KEYS.CALCULATOR_INPUTS);
    localStorage.removeItem(STORAGE_KEYS.CALCULATOR_WIZARD_STEP);
    localStorage.removeItem(STORAGE_KEYS.LAST_CALCULATOR_TAB);
    
    // Clear deprecated results keys (both versions)
    localStorage.removeItem(STORAGE_KEYS.CALCULATOR_RESULTS_V1);
    localStorage.removeItem(STORAGE_KEYS.CALCULATOR_RESULTS_OLD);
    
    // Clear custom pricing for all currencies
    for (const currency of SUPPORTED_CURRENCIES) {
      localStorage.removeItem(`${STORAGE_KEYS.CUSTOM_PRICING_PREFIX}-${currency}`);
    }
    
    console.log('Calculator state cleared successfully');
  } catch (error) {
    console.warn('Failed to clear calculator state from localStorage:', error);
  }
}

/**
 * Clears deprecated calculator localStorage keys on app mount.
 * Used for migration cleanup - removes legacy data that is no longer persisted:
 * - Calculator results (now always calculated fresh)
 * - Wizard step (now always starts at step 0)
 */
export function cleanupDeprecatedResultsStorage(): void {
  if (typeof window === 'undefined') {
    return; // Skip during SSR
  }
  
  try {
    let cleaned = false;
    
    // Check and remove deprecated results keys
    if (localStorage.getItem(STORAGE_KEYS.CALCULATOR_RESULTS_V1) !== null) {
      localStorage.removeItem(STORAGE_KEYS.CALCULATOR_RESULTS_V1);
      cleaned = true;
    }
    if (localStorage.getItem(STORAGE_KEYS.CALCULATOR_RESULTS_OLD) !== null) {
      localStorage.removeItem(STORAGE_KEYS.CALCULATOR_RESULTS_OLD);
      cleaned = true;
    }
    
    // Check and remove deprecated wizard step key
    if (localStorage.getItem(STORAGE_KEYS.CALCULATOR_WIZARD_STEP) !== null) {
      localStorage.removeItem(STORAGE_KEYS.CALCULATOR_WIZARD_STEP);
      cleaned = true;
    }
    
    if (cleaned) {
      console.log('Deprecated calculator storage cleaned up');
    }
  } catch (error) {
    console.warn('Failed to cleanup deprecated storage:', error);
  }
}

// Valid calculator tab types
export type CalculatorTab = 'wizard' | 'detailed' | 'simple';

/**
 * Get the last used calculator tab from localStorage
 */
export function getLastCalculatorTab(): CalculatorTab {
  if (typeof window === 'undefined') {
    return 'wizard'; // Default for SSR
  }
  
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.LAST_CALCULATOR_TAB);
    if (stored && ['wizard', 'detailed', 'simple'].includes(stored)) {
      return stored as CalculatorTab;
    }
  } catch (error) {
    console.warn('Failed to read last calculator tab from localStorage:', error);
  }
  
  return 'wizard'; // Default fallback
}

/**
 * Save the last used calculator tab to localStorage
 */
export function setLastCalculatorTab(tab: CalculatorTab): void {
  if (typeof window === 'undefined') {
    return; // Skip during SSR
  }
  
  try {
    localStorage.setItem(STORAGE_KEYS.LAST_CALCULATOR_TAB, tab);
  } catch (error) {
    console.warn('Failed to save last calculator tab to localStorage:', error);
  }
}

/**
 * React hook to manage persistent last calculator tab state
 */
import { useState, useEffect, useCallback } from 'react';

export function useLastCalculatorTab() {
  const [lastCalculatorTab, setLastCalculatorTabState] = useState<CalculatorTab>('wizard');

  // Initialize from localStorage on mount
  useEffect(() => {
    setLastCalculatorTabState(getLastCalculatorTab());
  }, []);

  // Update both state and localStorage - stable function reference
  const updateLastCalculatorTab = useCallback((tab: CalculatorTab) => {
    setLastCalculatorTabState(tab);
    setLastCalculatorTab(tab);
  }, []);

  return {
    lastCalculatorTab,
    setLastCalculatorTab: updateLastCalculatorTab,
  };
}